import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Chip, CircularProgress, Typography } from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import RefreshIcon from '@mui/icons-material/Refresh'
import instance from '../../../../../../../providers/axios.config'
import { typeResponse } from '../../../../../../../utils/types'
import Notiflix from 'notiflix'
import styles from './TaxCenter.module.scss'
import { TaxFormWizard } from './TaxFormWizard'
import { formatDate } from '../../../../../../../utils/helpers'

/**
 * Interval between polling cycles while at least one tax form has its
 * PDF still generating in the background. 3s balances UI responsiveness
 * with avoiding unnecessary API load.
 */
const PDF_POLL_INTERVAL_MS = 3000

/**
 * Safety cap on total polling time. After this duration we stop polling
 * even if a PDF never becomes ready, so a stuck queue worker does not
 * keep the client hammering the API forever. The seller can always
 * refresh the page manually to restart polling.
 */
const PDF_POLL_MAX_DURATION_MS = 2 * 60 * 1000

interface AvailableType {
  code: string
  label: string
  description: string
  is_entity: boolean
}

interface TaxFormRecord {
  id: number
  uuid: string
  form_type: string | null
  type: string | null
  status: string
  country: { id: number; name: string; code: string } | null
  tax_identification_number: string | null
  withholding_rate: number | null
  submitted_at: string | null
  approved_at: string | null
  declined_at: string | null
  declined_reason: string | null
  expire_at: string | null
  is_currently_valid: boolean
  is_pdf_processing: boolean
  created_at: string
}

/**
 * TaxCenter — redesigned seller tax center page.
 *
 * Features:
 *   - Lists the seller's tax forms with status badges and PDF downloads
 *   - Launches a multi-step wizard to submit a new form
 *   - Automatically polls the API while any form's PDF is still being
 *     generated in the background, so the seller does not need to
 *     manually refresh to see when their PDF is ready
 *
 * Polling lifecycle:
 *   1. After initial fetch, if any record has is_pdf_processing=true,
 *      a setInterval is started to silently re-fetch the list.
 *   2. The effect restarts or stops the interval whenever taxForms
 *      changes — once all processing flags clear, the interval cleans up.
 *   3. A maximum duration (PDF_POLL_MAX_DURATION_MS) prevents runaway
 *      polling when the queue worker is down.
 */
export const TaxCenter: React.FC = () => {
  const { t } = useTranslation()

  const [taxForms, setTaxForms] = useState<TaxFormRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [recommendedType, setRecommendedType] = useState<string | null>(null)
  const [availableTypes, setAvailableTypes] = useState<AvailableType[]>([])
  const [showWizard, setShowWizard] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  /**
   * Ref to the polling timeout so we can clean it up on unmount or when
   * the processing state changes. Using a ref avoids triggering re-renders
   * when we schedule/cancel the polling cycle.
   */
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  /**
   * Timestamp when polling started, used to enforce the hard cap on
   * total polling duration.
   */
  const pollingStartedAtRef = useRef<number | null>(null)

  /**
   * Fetch the seller's tax form history and (optionally) the recommended
   * form type. Silent mode suppresses the loading spinner so polling
   * refreshes stay invisible.
   */
  const fetchTaxForms = useCallback(async (silent = false): Promise<void> => {
    if (!silent) setLoading(true)

    try {
      const formsResp = await instance.get(
        'seller/tax-center/tax-forms'
      ) as typeResponse

      if ((formsResp as any).status === 'success') {
        setTaxForms((formsResp as any).data?.tax_forms ?? [])
      }
    } catch (error) {
      console.error('TaxCenter: failed to fetch tax forms', error)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  /**
   * Fetch recommended type and available types (only on mount — this
   * data doesn't change while the seller is on the page).
   */
  const fetchRecommendedType = useCallback(async (): Promise<void> => {
    try {
      const typeResp = await instance.get(
        'seller/tax-center/tax-forms/recommended-type'
      ) as typeResponse

      if ((typeResp as any).status === 'success') {
        setRecommendedType((typeResp as any).data?.recommended_type ?? null)
        setAvailableTypes((typeResp as any).data?.available_types ?? [])
      }
    } catch (error) {
      console.error('TaxCenter: failed to fetch recommended type', error)
    }
  }, [])

  /**
   * Initial load — fetch both endpoints in parallel.
   */
  useEffect(() => {
    (async () => {
      await Promise.all([fetchTaxForms(false), fetchRecommendedType()])
    })()
  }, [fetchTaxForms, fetchRecommendedType])

  /**
   * Polling effect — starts a setInterval when any tax form has
   * is_pdf_processing=true, and tears it down when all PDFs are ready
   * or the max duration elapses.
   *
   * Cleanup runs on unmount and on every taxForms change, so we never
   * leak intervals across re-renders.
   */
  useEffect(() => {
    const hasProcessing = taxForms.some((form) => form.is_pdf_processing)

    // If nothing is processing, make sure any running interval is cleared.
    if (!hasProcessing) {
      if (pollingTimerRef.current !== null) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
        pollingStartedAtRef.current = null
      }
      return
    }

    // Start the timer if not already running.
    if (pollingTimerRef.current === null) {
      pollingStartedAtRef.current = Date.now()

      pollingTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - (pollingStartedAtRef.current ?? Date.now())

        if (elapsed > PDF_POLL_MAX_DURATION_MS) {
          if (pollingTimerRef.current !== null) {
            clearInterval(pollingTimerRef.current)
            pollingTimerRef.current = null
            pollingStartedAtRef.current = null
          }
          return
        }

        fetchTaxForms(true)
      }, PDF_POLL_INTERVAL_MS)
    }

    // Cleanup on unmount or when dependencies change.
    return () => {
      if (pollingTimerRef.current !== null) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
        pollingStartedAtRef.current = null
      }
    }
  }, [taxForms, fetchTaxForms])

  /**
   * Download a generated PDF via temporary signed URL.
   */
  const handleDownload = async (formId: number) => {
    setDownloadingId(formId)
    try {
      const resp = await instance.get(
        `seller/tax-center/tax-forms/download/${formId}`
      ) as typeResponse

      if ((resp as any).status === 'success' && (resp as any).data?.download_url) {
        window.open((resp as any).data.download_url, '_blank')
      }
    } catch (error) {
      Notiflix.Notify.failure(t('pdf_not_ready'))
    } finally {
      setDownloadingId(null)
    }
  }

  /**
   * Called when the wizard completes a successful submission.
   * Refreshes the list so the new form (with is_pdf_processing=true)
   * appears immediately and polling kicks in automatically.
   */
  const handleWizardComplete = () => {
    setShowWizard(false)
    fetchTaxForms(false)
    Notiflix.Notify.success(t('tax_form_submitted_successfully'))
  }

  /**
   * Map status to visual properties.
   */
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          label: t('approved'),
          color: '#2e7d32' as const,
          bgColor: '#e8f5e9',
          icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
        }
      case 'denied':
        return {
          label: t('declined'),
          color: '#c11b1e' as const,
          bgColor: '#fce4ec',
          icon: <CancelOutlinedIcon sx={{ fontSize: 16 }} />,
        }
      default:
        return {
          label: t('pending_review'),
          color: '#e65100' as const,
          bgColor: '#fff3e0',
          icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
        }
    }
  }

  const getFormTypeLabel = (record: TaxFormRecord): string => {
    const type = record.form_type || record.type
    switch (type) {
      case 'w9':     return 'Form W-9'
      case 'w8ben':  return 'Form W-8BEN'
      case 'w8bene': return 'Form W-8BEN-E'
      default:       return type?.toUpperCase() || t('unknown')
    }
  }

  // ──────────────────────────────────────────────────────────────────
  //  Render: Wizard mode
  // ──────────────────────────────────────────────────────────────────

  if (showWizard) {
    return (
      <div className={styles.container}>
        <TaxFormWizard
          recommendedType={recommendedType}
          availableTypes={availableTypes}
          onComplete={handleWizardComplete}
          onCancel={() => setShowWizard(false)}
        />
      </div>
    )
  }

  // ──────────────────────────────────────────────────────────────────
  //  Render: Main listing
  // ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <DescriptionOutlinedIcon className={styles.headerIcon} />
          <div>
            <Typography className={styles.title}>{t('tax_center')}</Typography>
            <Typography className={styles.subtitle}>
              {t('tax_center_description')}
            </Typography>
          </div>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          className={styles.submitButton}
          onClick={() => setShowWizard(true)}
          disabled={loading}
        >
          {t('submit_new_tax_form')}
        </Button>
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <CircularProgress size={32} sx={{ color: '#c11b1e' }} />
        </div>
      )}

      {!loading && taxForms.length === 0 && (
        <div className={styles.emptyState}>
          <DescriptionOutlinedIcon className={styles.emptyIcon} />
          <Typography className={styles.emptyTitle}>
            {t('no_tax_forms_submitted')}
          </Typography>
          <Typography className={styles.emptyDescription}>
            {t('no_tax_forms_description')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={styles.submitButton}
            onClick={() => setShowWizard(true)}
          >
            {t('submit_your_first_tax_form')}
          </Button>
        </div>
      )}

      {!loading && taxForms.length > 0 && (
        <div className={styles.formsList}>
          {taxForms.map((record) => {
            const statusConfig = getStatusConfig(record.status)

            return (
              <div key={record.id} className={styles.formCard}>
                <div className={styles.cardTop}>
                  <div className={styles.cardInfo}>
                    <Typography className={styles.formType}>
                      {getFormTypeLabel(record)}
                    </Typography>
                    {record.country && (
                      <Typography className={styles.formCountry}>
                        {record.country.name}
                      </Typography>
                    )}
                  </div>
                  <Chip
                    icon={statusConfig.icon}
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      color: statusConfig.color,
                      backgroundColor: statusConfig.bgColor,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': { color: statusConfig.color },
                    }}
                  />
                </div>

                <div className={styles.cardDetails}>
                  {record.submitted_at && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('submitted')}</span>
                      <span className={styles.detailValue}>
                        {formatDate(record.submitted_at)}
                      </span>
                    </div>
                  )}
                  {record.tax_identification_number && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('tin')}</span>
                      <span className={styles.detailValue}>
                        {record.tax_identification_number.replace(
                          /^(.{3}).+(.{3})$/,
                          '$1•••$2'
                        )}
                      </span>
                    </div>
                  )}
                  {record.withholding_rate !== null && record.withholding_rate !== undefined && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('withholding_rate')}</span>
                      <span className={styles.detailValue}>
                        {(record.withholding_rate * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                  {record.expire_at && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('expires')}</span>
                      <span className={styles.detailValue}>
                        {formatDate(record.expire_at)}
                      </span>
                    </div>
                  )}
                </div>

                {record.status === 'denied' && record.declined_reason && (
                  <div className={styles.declineReason}>
                    <Typography className={styles.declineLabel}>
                      {t('decline_reason')}:
                    </Typography>
                    <Typography className={styles.declineText}>
                      {record.declined_reason}
                    </Typography>
                  </div>
                )}

                <div className={styles.cardActions}>
                  {record.is_pdf_processing ? (
                    <Chip
                      icon={<CircularProgress size={12} sx={{ color: '#e65100' }} />}
                      label={t('pdf_generating')}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ) : (
                    <Button
                      size="small"
                      startIcon={
                        downloadingId === record.id ? (
                          <CircularProgress size={14} sx={{ color: '#c11b1e' }} />
                        ) : (
                          <DownloadIcon />
                        )
                      }
                      className={styles.downloadButton}
                      onClick={() => handleDownload(record.id)}
                      disabled={downloadingId === record.id}
                    >
                      {t('download_pdf')}
                    </Button>
                  )}

                  {record.status === 'denied' && (
                    <Button
                      size="small"
                      startIcon={<RefreshIcon />}
                      className={styles.resubmitButton}
                      onClick={() => setShowWizard(true)}
                    >
                      {t('resubmit')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default TaxCenter